import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { History } from 'history';
import queryString, { ParsedQuery } from 'query-string';
import { PageLoadingIndicator } from '@spotify-internal/anchor-ds';

import { PODCAST_COLORS } from 'client/utils';
import { useCurrentUserCtx } from 'client/contexts/CurrentUser';
import { localStorage } from 'client/modules/Browser/localStorage';
import { useCreateEpisode } from 'hooks/episodes/useCreateEpisode';
import {
  IntegrationRedirectStates,
  INTEGRATION_LANDING_PAGE,
  INTEGRATION_REDIRECT_STATES,
  IS_EXISTING_USER_KEY,
  VALID_VIDEO_PARTNERS,
} from './constants';
import { IntegrationRedirectScreenError } from './components';

const isPartnerValid = (
  integrationName: string,
  integrationData: ParsedQuery<string>
) => {
  return (
    Object.keys(VALID_VIDEO_PARTNERS).includes(integrationName) &&
    VALID_VIDEO_PARTNERS[integrationName].requiredParams.every(
      (key: string) => !!integrationData[key]
    )
  );
};

export function IntergrationRedirectScreen({ history }: { history: History }) {
  const [currentState, setCurrentState] = useState<IntegrationRedirectStates>(
    INTEGRATION_REDIRECT_STATES.IDLE
  );
  const {
    state: { status, userId, webStationId },
  } = useCurrentUserCtx();
  const { createNewEpisode } = useCreateEpisode();
  const { search } = useLocation();
  const { integrationName } = useParams<{ integrationName: string }>();
  const integrationData = queryString.parse(decodeURIComponent(search));
  const isPartnerAndDataValid = isPartnerValid(
    integrationName,
    integrationData
  );

  // If any step has failed, we redirect them to the integration
  // landing page, while passing the riverside data as query params
  function loggedOutRedirect() {
    setCurrentState(INTEGRATION_REDIRECT_STATES.FAIL);
    localStorage.getItem(IS_EXISTING_USER_KEY).then(isExistingUser => {
      // TODO https://anchorfm.atlassian.net/browse/CAST-2761: Add Integration Name when redirecting
      // to lander so we can redirect back here correctly and know what integration it is
      const queryParamObject = {
        ...integrationData,
        ...(!isExistingUser && { create: true }),
      };
      const queryParamString = encodeURIComponent(
        queryString.stringify(queryParamObject)
      );
      history.replace(`${INTEGRATION_LANDING_PAGE}?${queryParamString}`);
    });
  }

  // When all steps have been successful, we create a new shell episode and redirect
  // to Episode Options page for new episode
  function handleSuccess() {
    setCurrentState(INTEGRATION_REDIRECT_STATES.SUCCESS);
    createNewEpisode(
      {},
      {
        onSuccess: data => {
          const queryParamString = encodeURIComponent(
            queryString.stringify(integrationData)
          );
          history.push(
            `/dashboard/episode/${data.webEpisodeId}/metadata/edit?${queryParamString}`
          );
        },
      }
    );
  }

  // TODO https://anchorfm.atlassian.net/browse/CAST-2761: Connect BE Endpoints and flush out final checks
  // The flow for the below should run as follows:
  // * If status is "success" and the user has the specific integration on their record, envoke handleSuccess()
  // * If status is "success" and the user does not have a specific integration on their record, envoke loggedOutRedirect()
  // * If status is "success" but we have no user or there is an error, envoke loggedOutRedirect()
  useEffect(() => {
    if (isPartnerAndDataValid) {
      if (
        status === 'success' &&
        currentState === INTEGRATION_REDIRECT_STATES.IDLE
      ) {
        if (userId && webStationId) {
          handleSuccess();
        } else {
          loggedOutRedirect();
        }
      } else if (status === 'error') {
        loggedOutRedirect();
      }
    }
  }, [
    currentState,
    handleSuccess,
    isPartnerAndDataValid,
    loggedOutRedirect,
    status,
    userId,
    webStationId,
  ]);

  return isPartnerAndDataValid ? (
    <PageLoadingIndicator color={PODCAST_COLORS.WHITE.color} />
  ) : (
    <IntegrationRedirectScreenError integrationName={integrationName} />
  );
}
